import { imageURL } from "../index";

/**
 * Fetches a protected image with authentication and returns a blob URL
 * @param imagePath - The path to the image (e.g., "menu-items/deny.gif")
 * @param token - JWT token for authentication (optional, will use localStorage if not provided)
 * @returns Promise<string> - Blob URL that can be used in img src
 */
export const fetchProtectedImage = async (
  imagePath: string,
  token?: string
): Promise<string> => {
  // Get token from localStorage if not provided
  const authToken = token || localStorage.getItem("token");

  if (!authToken) {
    throw new Error("No authentication token available");
  }

  try {
    const response = await fetch(
      `${imageURL}?path=${encodeURIComponent(imagePath)}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(`Error fetching protected image: ${imagePath}`, error);
    throw error;
  }
};

/**
 * Fetches multiple protected images and returns a map of image paths to blob URLs
 * @param imagePaths - Array of image paths to fetch
 * @param token - JWT token for authentication (optional)
 * @returns Promise<Record<string, string>> - Map of image paths to blob URLs
 */
export const fetchProtectedImages = async (
  imagePaths: string[],
  token?: string
): Promise<Record<string, string>> => {
  const imageUrls: Record<string, string> = {};

  await Promise.all(
    imagePaths.map(async (imagePath) => {
      try {
        const blobUrl = await fetchProtectedImage(imagePath, token);
        imageUrls[imagePath] = blobUrl;
      } catch (error) {
        console.error(`Failed to load image: ${imagePath}`, error);
        // Continue loading other images even if one fails
      }
    })
  );

  return imageUrls;
};

/**
 * Revokes blob URLs to free up memory
 * @param blobUrls - Array of blob URLs or a map of blob URLs
 */
export const revokeImageUrls = (
  blobUrls: string[] | Record<string, string>
): void => {
  if (Array.isArray(blobUrls)) {
    blobUrls.forEach((url) => URL.revokeObjectURL(url));
  } else {
    Object.values(blobUrls).forEach((url) => URL.revokeObjectURL(url));
  }
};
